/* ─── Supabase blog-stats helpers ──────────────────────────────
   Uses raw fetch against the Supabase PostgREST API so no SDK
   is required. Falls back gracefully when env vars are absent
   (local dev without Supabase, or before setup).

   Required env vars:
     REACT_APP_SUPABASE_URL      e.g. https://xyz.supabase.co
     REACT_APP_SUPABASE_ANON_KEY  your project's anon/public key

   Required Supabase SQL (run once in the SQL Editor):
   ──────────────────────────────────────────────────────────────
   create table if not exists blog_stats (
     post_id text primary key,
     likes   integer not null default 0,
     shares  integer not null default 0
   );

   alter table blog_stats enable row level security;

   create policy "public read"   on blog_stats for select using (true);
   create policy "public insert" on blog_stats for insert with check (true);
   create policy "public update" on blog_stats for update using (true);

   create or replace function increment_stat(p_post_id text, p_field text)
   returns void language plpgsql security definer as $$
   begin
     insert into blog_stats (post_id, likes, shares)
     values (p_post_id, 0, 0)
     on conflict (post_id) do nothing;

     if p_field = 'likes' then
       update blog_stats set likes = likes + 1 where post_id = p_post_id;
     elsif p_field = 'shares' then
       update blog_stats set shares = shares + 1 where post_id = p_post_id;
     end if;
   end;
   $$;

   create or replace function decrement_likes(p_post_id text)
   returns void language plpgsql security definer as $$
   begin
     update blog_stats
     set likes = greatest(0, likes - 1)
     where post_id = p_post_id;
   end;
   $$;

   grant execute on function increment_stat to anon;
   grant execute on function decrement_likes to anon;
   ──────────────────────────────────────────────────────────────
*/

var SUPABASE_URL     = process.env.REACT_APP_SUPABASE_URL     || "";
var SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

function isConfigured() {
  return !!(SUPABASE_URL && SUPABASE_ANON_KEY);
}

function headers() {
  return {
    "Content-Type":  "application/json",
    "apikey":        SUPABASE_ANON_KEY,
    "Authorization": "Bearer " + SUPABASE_ANON_KEY,
  };
}

/**
 * Fetch like + share counts for a single post.
 * Returns { likes: number, shares: number } or null on failure / not configured.
 */
export async function getStats(postId) {
  if (!isConfigured()) return null;
  try {
    var res = await fetch(
      SUPABASE_URL + "/rest/v1/blog_stats?post_id=eq." +
        encodeURIComponent(postId) + "&select=likes,shares",
      { headers: headers() }
    );
    if (!res.ok) return null;
    var rows = await res.json();
    if (rows && rows[0]) return { likes: rows[0].likes, shares: rows[0].shares };
    /* Row doesn't exist yet  -  insert a seed row so future RPCs find it */
    await fetch(SUPABASE_URL + "/rest/v1/blog_stats", {
      method:  "POST",
      headers: Object.assign({}, headers(), { Prefer: "return=minimal" }),
      body:    JSON.stringify({ post_id: postId, likes: 0, shares: 0 }),
    });
    return { likes: 0, shares: 0 };
  } catch(e) {
    return null;
  }
}

/**
 * Atomically increment a field ("likes" or "shares").
 */
export async function incrementStat(postId, field) {
  if (!isConfigured()) return;
  try {
    await fetch(SUPABASE_URL + "/rest/v1/rpc/increment_stat", {
      method:  "POST",
      headers: headers(),
      body:    JSON.stringify({ p_post_id: postId, p_field: field }),
    });
  } catch(e) {}
}

/**
 * Atomically decrement likes (floors at 0).
 */
export async function decrementLikes(postId) {
  if (!isConfigured()) return;
  try {
    await fetch(SUPABASE_URL + "/rest/v1/rpc/decrement_likes", {
      method:  "POST",
      headers: headers(),
      body:    JSON.stringify({ p_post_id: postId }),
    });
  } catch(e) {}
}
